import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  InnocodeButton,
  useDialog,
  useInnocodeLoadingOverlay,
} from "@intsoft/innocode-core";
import { ssoAdminApi } from "../../api";
import styles from "../ServiceGroups.module.css";

const normalizeRelationType = (value) => {
  const relationType = String(value || "").trim().toUpperCase();
  if (relationType === "OWNER") {
    return "OWNER";
  }
  if (relationType === "PARTICIPANT") {
    return "PARTICIPANT";
  }
  return "";
};

const normalizeMember = (member) => ({
  ...member,
  memberId: member?.memberId || member?.id || "",
  clientId: member?.clientId || member?.serviceId || "",
  serviceName: member?.serviceName || member?.clientName || "",
  tenantId: member?.tenantId || member?.ownerTenantId || "",
  tenantName: member?.tenantName || member?.ownerTenantName || "",
  status: member?.status || "",
});

const normalizeCatalogItem = (item) => ({
  ...item,
  clientId: item?.clientId || item?.serviceId || "",
  serviceName: item?.serviceName || item?.clientName || "",
  tenantId: item?.tenantId || item?.ownerTenantId || "",
  tenantName: item?.tenantName || item?.ownerTenantName || "",
  addable: typeof item?.addable === "boolean" ? item.addable : true,
  status: item?.status || "",
});

const normalizeCatalogResult = (result) => {
  if (Array.isArray(result)) {
    return result.map(normalizeCatalogItem);
  }
  if (Array.isArray(result?.content)) {
    return result.content.map(normalizeCatalogItem);
  }
  if (Array.isArray(result?.items)) {
    return result.items.map(normalizeCatalogItem);
  }
  if (Array.isArray(result?.data)) {
    return result.data.map(normalizeCatalogItem);
  }
  return [];
};

const buildServiceKey = (item) => `${item?.tenantId || ""}:${item?.clientId || ""}`;

const filterVisibleMembers = (members, relationType, ownerTenantId, contextTenantId) => {
  if (!Array.isArray(members)) {
    return [];
  }
  if (relationType === "OWNER") {
    return members.filter((member) => member.tenantId === ownerTenantId);
  }
  return members.filter(
    (member) =>
      member.tenantId === ownerTenantId || member.tenantId === contextTenantId
  );
};

function ServiceGroupServiceManageModal({
  groupId,
  contextTenantId,
  contextTenantName,
  relationType,
  onClose,
  onSaved,
}) {
  const { showDialog } = useDialog();
  const { show: showLoading, hide: hideLoading } = useInnocodeLoadingOverlay();
  const initialLoadGroupIdRef = useRef(null);
  const [detail, setDetail] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [currentMembers, setCurrentMembers] = useState([]);
  const [pendingRemoves, setPendingRemoves] = useState([]);

  const normalizedGroupId =
    typeof groupId === "object" && groupId !== null
      ? groupId.groupId || groupId.id || ""
      : groupId;
  const normalizedContextTenantId =
    typeof contextTenantId === "object" && contextTenantId !== null
      ? contextTenantId.tenantId || contextTenantId.id || ""
      : contextTenantId || "";
  const normalizedRelationType = normalizeRelationType(relationType);

  const loadDetail = useCallback(async () => {
    if (!normalizedGroupId) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "서비스 관리 실패",
        message: "서비스 그룹 ID가 올바르지 않습니다.",
        confirmText: "확인",
        onConfirm: onClose,
      });
      return;
    }

    showLoading();
    try {
      const [detailResult, catalogResult] = await Promise.all([
        ssoAdminApi.fetchServiceGroupDetail(normalizedGroupId, {
          ...(normalizedContextTenantId
            ? { contextTenantId: normalizedContextTenantId }
            : {}),
          ...(normalizedRelationType ? { relationType: normalizedRelationType } : {}),
        }),
        ssoAdminApi.fetchServiceGroupMemberCatalog(normalizedGroupId, {
          ...(normalizedContextTenantId
            ? { contextTenantId: normalizedContextTenantId }
            : {}),
          ...(normalizedRelationType ? { relationType: normalizedRelationType } : {}),
        }),
      ]);

      const normalizedDetail = {
        ...detailResult,
        groupId: detailResult?.groupId || normalizedGroupId,
        groupKey: detailResult?.groupKey || "",
        groupName: detailResult?.groupName || "",
        ownerTenantId: detailResult?.ownerTenantId || "",
        ownerTenantName: detailResult?.ownerTenantName || "",
        contextTenantId: detailResult?.contextTenantId || normalizedContextTenantId,
        contextTenantName: detailResult?.contextTenantName || contextTenantName || "",
        relationType: normalizeRelationType(
          detailResult?.relationType || detailResult?.relation || normalizedRelationType
        ),
        members: Array.isArray(detailResult?.members)
          ? detailResult.members.map(normalizeMember)
          : [],
      };

      const effectiveContextTenantId =
        normalizedDetail.contextTenantId || normalizedDetail.ownerTenantId;

      const ownCatalog = normalizeCatalogResult(catalogResult).filter(
        (item) =>
          item.addable !== false &&
          (!effectiveContextTenantId || item.tenantId === effectiveContextTenantId)
      );

      const visibleMembers = filterVisibleMembers(
        normalizedDetail.members,
        normalizedDetail.relationType,
        normalizedDetail.ownerTenantId,
        effectiveContextTenantId
      );

      setDetail(normalizedDetail);
      setCatalog(ownCatalog);
      setCurrentMembers(visibleMembers);
      setPendingRemoves([]);
    } catch (error) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "서비스 관리 실패",
        message:
          error?.response?.data?.message ||
          "서비스 그룹 멤버 정보를 불러오지 못했습니다.",
        confirmText: "확인",
        onConfirm: onClose,
      });
    } finally {
      hideLoading();
    }
  }, [
    contextTenantName,
    hideLoading,
    normalizedContextTenantId,
    normalizedGroupId,
    normalizedRelationType,
    onClose,
    showDialog,
    showLoading,
  ]);

  useEffect(() => {
    const loadKey = normalizedGroupId == null ? "" : String(normalizedGroupId);
    if (initialLoadGroupIdRef.current === loadKey) {
      return;
    }

    initialLoadGroupIdRef.current = loadKey;
    loadDetail();
  }, [loadDetail, normalizedGroupId]);

  const effectiveContextTenantId = useMemo(
    () =>
      detail?.contextTenantId ||
      normalizedContextTenantId ||
      detail?.ownerTenantId ||
      "",
    [detail?.contextTenantId, detail?.ownerTenantId, normalizedContextTenantId]
  );

  const availableServices = useMemo(() => {
    const currentServiceKeys = new Set(currentMembers.map(buildServiceKey));
    return catalog.filter((service) => {
      if (!service.clientId || !service.tenantId) {
        return false;
      }
      if (service.tenantId !== effectiveContextTenantId) {
        return false;
      }
      return !currentServiceKeys.has(buildServiceKey(service));
    });
  }, [catalog, currentMembers, effectiveContextTenantId]);

  const canRemoveMember = useCallback(
    (member) => {
      if (!member) {
        return false;
      }
      if (String(member.memberId || "").startsWith("pending:")) {
        return true;
      }
      return (
        member.memberId &&
        member.tenantId &&
        effectiveContextTenantId &&
        member.tenantId === effectiveContextTenantId
      );
    },
    [effectiveContextTenantId]
  );

  const handleAddMember = useCallback((service) => {
    const pendingMember = {
      ...service,
      memberId: `pending:${buildServiceKey(service)}`,
      isPending: true,
    };

    setCurrentMembers((prev) => {
      if (prev.some((member) => buildServiceKey(member) === buildServiceKey(service))) {
        return prev;
      }
      return [...prev, pendingMember];
    });
  }, []);

  const handleRemoveMember = useCallback(
    (member) => {
      if (!canRemoveMember(member)) {
        return;
      }

      setCurrentMembers((prev) =>
        prev.filter((item) => item.memberId !== member.memberId)
      );

      if (String(member?.memberId || "").startsWith("pending:")) {
        return;
      }

      setPendingRemoves((prev) => {
        if (prev.some((item) => item.memberId === member.memberId)) {
          return prev;
        }
        return [...prev, member];
      });
    },
    [canRemoveMember]
  );

  const handleSave = async () => {
    if (!detail?.groupId) {
      return;
    }

    const pendingAdds = currentMembers.filter((member) => member.isPending);

    if (pendingAdds.length === 0 && pendingRemoves.length === 0) {
      showDialog({
        iconType: "warning",
        type: "info",
        title: "변경 없음",
        message: "저장할 서비스 변경 사항이 없습니다.",
        confirmText: "확인",
      });
      return;
    }

    showLoading();

    try {
      for (const service of pendingAdds) {
        await ssoAdminApi.addServiceGroupMember(detail.groupId, {
          tenantId: service.tenantId,
          clientId: service.clientId,
          serviceName: service.serviceName,
        });
      }

      for (const member of pendingRemoves) {
        await ssoAdminApi.deleteServiceGroupMember(detail.groupId, member.memberId);
      }

      await loadDetail();
      onSaved();
      showDialog({
        iconType: "check",
        type: "info",
        title: "저장 완료",
        message: "서비스 구성 변경이 반영되었습니다.",
        confirmText: "확인",
      });
    } catch (error) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "저장 실패",
        message:
          error?.response?.data?.message ||
          "서비스 구성 저장 중 오류가 발생했습니다.",
        confirmText: "확인",
      });
    } finally {
      hideLoading();
    }
  };

  return (
    <div className={styles.modalLayout}>
      <div className={`${styles.panel} ${styles.rulePanel}`}>
        <p className={styles.ruleText}>
          참여 서비스는 관계 기준으로만 보여지며, 오너 행은 오너 서비스만, 파트너 행은
          오너 서비스와 참여 테넌트 본인 서비스만 볼 수 있도록 합니다.
        </p>
        <p className={styles.ruleText}>
          추가 가능한 서비스는 항상 현재 테넌트(오너 혹은 참여자)의 서비스만 후보로
          보여 줍니다.
        </p>
      </div>

      {detail ? (
        <>
          <div className={`${styles.detailContentLayout} ${styles.serviceManageLayout}`}>
            <section className={styles.panel}>
              <div className={styles.sectionHeader}>
                <div>
                  <h3 className={styles.sectionTitle}>현재 등록 서비스</h3>
                  <p className={styles.sectionSubText}>
                    내 서비스는 곧바로 삭제할 수 있습니다.
                  </p>
                </div>
              </div>

              <div className={styles.selectionList}>
                {currentMembers.length > 0 ? (
                  currentMembers.map((member) => (
                    <div key={member.memberId} className={styles.selectionRow}>
                      <div>
                        <div className={styles.selectionText}>
                          {member.serviceName || member.clientId || "-"}
                        </div>
                        <div className={styles.selectionMeta}>
                          {member.tenantName || member.tenantId || "-"}
                          {member.status ? ` · ${member.status}` : ""}
                          {member.isPending ? " · 추가 예정" : ""}
                        </div>
                      </div>
                      {canRemoveMember(member) ? (
                        <InnocodeButton
                          size="sm"
                          theme="line"
                          designType="primary"
                          onClick={() => handleRemoveMember(member)}
                        >
                          삭제
                        </InnocodeButton>
                      ) : (
                        <span className={styles.selectionMeta}>삭제 불가</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyBox}>
                    현재 등록된 서비스가 없습니다.
                  </div>
                )}
              </div>
            </section>

            <section className={styles.panel}>
              <div className={styles.sectionHeader}>
                <div>
                  <h3 className={styles.sectionTitle}>추가 가능한 서비스</h3>
                  <p className={styles.sectionSubText}>
                    현재 테넌트 서비스만 선택할 수 있습니다.
                  </p>
                </div>
              </div>

              <div className={styles.selectionList}>
                {availableServices.length > 0 ? (
                  availableServices.map((service) => (
                    <div key={buildServiceKey(service)} className={styles.selectionRow}>
                      <div>
                        <div className={styles.selectionText}>
                          {service.serviceName || service.clientId || "-"}
                        </div>
                        <div className={styles.selectionMeta}>
                          {service.tenantName || service.tenantId || "-"}
                          {service.status ? ` · ${service.status}` : ""}
                        </div>
                      </div>
                      <InnocodeButton
                        size="sm"
                        onClick={() => handleAddMember(service)}
                      >
                        추가
                      </InnocodeButton>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyBox}>추가 가능한 서비스가 없습니다.</div>
                )}
              </div>
            </section>
          </div>

          <div className={styles.buttonRow}>
            <InnocodeButton
              theme="line"
              designType="primary"
              onClick={loadDetail}
            >
              초기화
            </InnocodeButton>
            <InnocodeButton onClick={handleSave}>저장</InnocodeButton>
          </div>
        </>
      ) : (
        <div className={styles.emptyBox}>서비스 정보를 불러오는 중입니다.</div>
      )}
    </div>
  );
}

export default ServiceGroupServiceManageModal;
